import * as React from 'react';

/*
 * Custom, on-brand animated SVG illustrations for the Highlights section.
 * Each uses the site's blue -> purple -> cyan gradient, unique gradient IDs
 * (so multiple can coexist), and CSS animations from global.css.
 * Animations auto-disable under prefers-reduced-motion (handled globally).
 */

const svgStyle = { width: '100%', height: '100%', display: 'block', overflow: 'visible' };

function Defs({ id }) {
  return (
    <defs>
      <linearGradient id={`${id}-grad`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="hsl(210,98%,58%)" />
        <stop offset="55%" stopColor="hsl(240,92%,64%)" />
        <stop offset="100%" stopColor="hsl(265,90%,64%)" />
      </linearGradient>
      <linearGradient id={`${id}-grad2`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="hsl(190,95%,55%)" />
        <stop offset="100%" stopColor="hsl(210,98%,56%)" />
      </linearGradient>
      <radialGradient id={`${id}-glow`} cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="hsl(230,95%,60%)" stopOpacity="0.55" />
        <stop offset="100%" stopColor="hsl(230,95%,60%)" stopOpacity="0" />
      </radialGradient>
      <filter id={`${id}-soft`} x="-40%" y="-40%" width="180%" height="180%">
        <feGaussianBlur stdDeviation="6" />
      </filter>
    </defs>
  );
}

/* ---------- 1. Cloud Applications ---------- */
export function CloudIllustration() {
  const id = 'fx-cloud';
  return (
    <svg viewBox="0 0 420 360" style={svgStyle} role="img" aria-label="Cloud applications illustration">
      <Defs id={id} />
      <circle cx="210" cy="175" r="150" fill={`url(#${id}-glow)`} />

      {/* orbiting dashed ring */}
      <g style={{ transformOrigin: '210px 175px', animation: 'fx-spin-slow 26s linear infinite' }}>
        <ellipse
          cx="210" cy="175" rx="165" ry="92"
          fill="none" stroke={`url(#${id}-grad2)`} strokeWidth="1.5"
          strokeDasharray="4 10" opacity="0.5"
        />
      </g>

      {/* connecting data lines */}
      <g stroke={`url(#${id}-grad)`} strokeWidth="2" fill="none" strokeLinecap="round"
         strokeDasharray="6 8" style={{ animation: 'fx-dash 12s linear infinite' }} opacity="0.85">
        <path d="M120 250 L165 205" />
        <path d="M300 250 L255 205" />
        <path d="M210 262 L210 218" />
      </g>

      {/* server / db nodes */}
      <g style={{ animation: 'fx-float-soft 5s ease-in-out infinite' }}>
        <rect x="86" y="248" width="70" height="54" rx="9" fill="hsl(224,45%,12%)" stroke={`url(#${id}-grad)`} strokeWidth="2" />
        <rect x="98" y="260" width="46" height="5" rx="2.5" fill="hsl(210,98%,60%)" />
        <rect x="98" y="271" width="34" height="5" rx="2.5" fill="hsl(210,30%,55%)" />
        <circle cx="139" cy="290" r="4" fill="hsl(150,70%,50%)" />
      </g>
      <g style={{ animation: 'fx-float-soft 5s ease-in-out infinite 0.6s' }}>
        <rect x="264" y="248" width="70" height="54" rx="9" fill="hsl(224,45%,12%)" stroke={`url(#${id}-grad2)`} strokeWidth="2" />
        <ellipse cx="299" cy="264" rx="20" ry="6" fill="none" stroke="hsl(190,95%,58%)" strokeWidth="2" />
        <path d="M279 264 v18 a20 6 0 0 0 40 0 v-18" fill="none" stroke="hsl(190,95%,58%)" strokeWidth="2" />
        <path d="M279 273 a20 6 0 0 0 40 0" fill="none" stroke="hsl(190,95%,58%)" strokeWidth="1.5" opacity="0.6" />
      </g>

      {/* main cloud */}
      <g style={{ animation: 'fx-float-soft 6s ease-in-out infinite' }}>
        <path
          d="M150 175 a44 44 0 0 1 86 -14 a34 34 0 0 1 32 46 h-118 a34 34 0 0 1 0 -34 z"
          fill={`url(#${id}-grad)`}
          filter={`url(#${id}-soft)`}
          opacity="0.55"
        />
        <path
          d="M150 172 a44 44 0 0 1 86 -14 a34 34 0 0 1 30 44 h-116 a32 32 0 0 1 0 -30 z"
          fill={`url(#${id}-grad)`}
        />
        {/* sync arrows on cloud */}
        <g stroke="#fff" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.95">
          <path d="M196 176 a16 16 0 0 1 28 -8" />
          <path d="M224 152 v16 h-16" />
          <path d="M224 196 a16 16 0 0 1 -28 8" />
          <path d="M196 220 v-16 h16" />
        </g>
      </g>

      {/* rising particles */}
      <circle cx="210" cy="120" r="3" fill="hsl(190,95%,60%)" style={{ animation: 'fx-rise 3.4s ease-in-out infinite' }} />
      <circle cx="176" cy="130" r="2.5" fill="hsl(265,90%,68%)" style={{ animation: 'fx-rise 3.4s ease-in-out infinite 1s' }} />
      <circle cx="244" cy="126" r="2.5" fill="hsl(210,98%,62%)" style={{ animation: 'fx-rise 3.4s ease-in-out infinite 1.8s' }} />
    </svg>
  );
}

/* ---------- 2. Mobile Applications ---------- */
export function MobileIllustration() {
  const id = 'fx-mobile';
  return (
    <svg viewBox="0 0 420 360" style={svgStyle} role="img" aria-label="Mobile applications illustration">
      <Defs id={id} />
      <circle cx="210" cy="180" r="150" fill={`url(#${id}-glow)`} />

      {/* back phone */}
      <g transform="rotate(-12 150 190)" style={{ animation: 'fx-float-soft 6s ease-in-out infinite 0.5s' }} opacity="0.5">
        <rect x="112" y="96" width="120" height="200" rx="22" fill="hsl(224,45%,14%)" stroke={`url(#${id}-grad2)`} strokeWidth="2" />
        <rect x="126" y="120" width="92" height="150" rx="10" fill="hsl(224,50%,10%)" />
      </g>

      {/* front phone */}
      <g style={{ animation: 'fx-float-soft 6s ease-in-out infinite' }}>
        <rect x="190" y="70" width="130" height="220" rx="26" fill="hsl(224,45%,12%)" stroke={`url(#${id}-grad)`} strokeWidth="2.5" />
        <rect x="202" y="84" width="106" height="192" rx="15" fill="hsl(226,52%,8%)" />
        {/* notch */}
        <rect x="238" y="90" width="34" height="7" rx="3.5" fill="hsl(224,30%,30%)" />
        {/* header */}
        <rect x="214" y="108" width="60" height="8" rx="4" fill="#fff" opacity="0.9" />
        <rect x="214" y="122" width="40" height="6" rx="3" fill="hsl(210,30%,55%)" />
        {/* app grid */}
        {[0, 1, 2].map((r) =>
          [0, 1, 2].map((c) => (
            <rect
              key={`${r}-${c}`}
              x={214 + c * 28} y={142 + r * 28} width="22" height="22" rx="6"
              fill={`url(#${(r + c) % 2 ? `${id}-grad2` : `${id}-grad`})`}
              opacity={0.55 + ((r + c) % 3) * 0.15}
            />
          ))
        )}
        {/* mini bar chart */}
        <g>
          <rect x="214" y="248" width="8" height="14" rx="2" fill="hsl(190,95%,58%)" />
          <rect x="226" y="240" width="8" height="22" rx="2" fill="hsl(210,98%,60%)" />
          <rect x="238" y="232" width="8" height="30" rx="2" fill="hsl(240,90%,64%)" />
          <rect x="250" y="244" width="8" height="18" rx="2" fill="hsl(265,90%,66%)" />
          <rect x="262" y="236" width="8" height="26" rx="2" fill="hsl(210,98%,62%)" />
          <rect x="274" y="250" width="8" height="12" rx="2" fill="hsl(190,95%,58%)" />
        </g>
      </g>

      {/* floating notifications */}
      <g style={{ animation: 'fx-rise 4s ease-in-out infinite' }}>
        <rect x="96" y="118" width="94" height="26" rx="13" fill="hsl(224,45%,14%)" stroke={`url(#${id}-grad)`} strokeWidth="1.5" />
        <circle cx="111" cy="131" r="7" fill="hsl(210,98%,60%)" />
        <rect x="124" y="126" width="52" height="4" rx="2" fill="#fff" opacity="0.85" />
        <rect x="124" y="134" width="34" height="4" rx="2" fill="hsl(210,30%,55%)" />
      </g>
      <g style={{ animation: 'fx-rise 4s ease-in-out infinite 2s' }}>
        <rect x="108" y="210" width="80" height="24" rx="12" fill="hsl(224,45%,14%)" stroke={`url(#${id}-grad2)`} strokeWidth="1.5" />
        <circle cx="122" cy="222" r="6" fill="hsl(150,70%,50%)" />
        <rect x="134" y="219" width="44" height="4" rx="2" fill="#fff" opacity="0.8" />
      </g>
    </svg>
  );
}

/* ---------- 3. Desktop Applications ---------- */
export function DesktopIllustration() {
  const id = 'fx-desktop';
  return (
    <svg viewBox="0 0 420 360" style={svgStyle} role="img" aria-label="Desktop applications illustration">
      <Defs id={id} />
      <circle cx="210" cy="165" r="155" fill={`url(#${id}-glow)`} />

      <g style={{ animation: 'fx-float-soft 7s ease-in-out infinite' }}>
        {/* monitor body */}
        <rect x="70" y="66" width="280" height="184" rx="16" fill="hsl(224,45%,12%)" stroke={`url(#${id}-grad)`} strokeWidth="2.5" />
        <rect x="84" y="80" width="252" height="156" rx="8" fill="hsl(226,52%,8%)" />

        {/* title bar */}
        <circle cx="100" cy="96" r="4" fill="hsl(0,80%,62%)" />
        <circle cx="114" cy="96" r="4" fill="hsl(45,90%,58%)" />
        <circle cx="128" cy="96" r="4" fill="hsl(140,70%,50%)" />

        {/* sidebar */}
        <rect x="92" y="110" width="52" height="118" rx="6" fill="hsl(224,45%,13%)" />
        <rect x="100" y="120" width="36" height="6" rx="3" fill={`url(#${id}-grad2)`} />
        <rect x="100" y="132" width="28" height="5" rx="2.5" fill="hsl(210,30%,45%)" />
        <rect x="100" y="142" width="32" height="5" rx="2.5" fill="hsl(210,30%,45%)" />
        <rect x="100" y="152" width="24" height="5" rx="2.5" fill="hsl(210,30%,45%)" />

        {/* code lines */}
        <g strokeLinecap="round">
          <rect x="158" y="118" width="30" height="6" rx="3" fill="hsl(265,90%,66%)" />
          <rect x="192" y="118" width="60" height="6" rx="3" fill="hsl(210,98%,60%)" />
          <rect x="170" y="132" width="44" height="6" rx="3" fill="hsl(190,95%,58%)" />
          <rect x="218" y="132" width="36" height="6" rx="3" fill="hsl(210,30%,50%)" />
          <rect x="170" y="146" width="70" height="6" rx="3" fill="hsl(150,70%,52%)" />
          <rect x="158" y="160" width="40" height="6" rx="3" fill="hsl(210,98%,60%)" />
          <rect x="202" y="160" width="50" height="6" rx="3" fill="hsl(265,90%,66%)" />
          <rect x="170" y="174" width="54" height="6" rx="3" fill="hsl(190,95%,58%)" />
          <rect x="158" y="188" width="34" height="6" rx="3" fill="hsl(210,30%,50%)" />
          {/* blinking cursor */}
          <rect x="198" y="186" width="3" height="10" rx="1.5" fill="#fff" style={{ animation: 'fx-blink 1.1s step-end infinite' }} />
        </g>

        {/* status bar */}
        <rect x="158" y="206" width="164" height="1.5" fill="hsl(224,30%,25%)" />
        <circle cx="166" cy="218" r="3" fill="hsl(150,70%,50%)" />
        <rect x="176" y="215" width="40" height="5" rx="2.5" fill="hsl(210,30%,45%)" />
      </g>

      {/* stand */}
      <path d="M190 250 h40 l8 34 h-56 z" fill="hsl(224,40%,16%)" stroke={`url(#${id}-grad)`} strokeWidth="1.5" />
      <rect x="160" y="284" width="100" height="9" rx="4.5" fill="hsl(224,40%,18%)" stroke={`url(#${id}-grad)`} strokeWidth="1.5" />

      {/* floating tooltip card */}
      <g style={{ animation: 'fx-float-soft 5s ease-in-out infinite 1s' }}>
        <rect x="286" y="150" width="86" height="52" rx="10" fill="hsl(224,45%,14%)" stroke={`url(#${id}-grad2)`} strokeWidth="2" />
        <circle cx="304" cy="168" r="8" fill={`url(#${id}-grad)`} />
        <rect x="318" y="162" width="44" height="5" rx="2.5" fill="#fff" opacity="0.85" />
        <rect x="318" y="171" width="30" height="5" rx="2.5" fill="hsl(210,30%,50%)" />
        <rect x="296" y="186" width="66" height="6" rx="3" fill={`url(#${id}-grad2)`} opacity="0.7" />
      </g>
    </svg>
  );
}

/* ---------- 4. End-to-End Development ---------- */
export function EndToEndIllustration() {
  const id = 'fx-e2e';
  const nodes = [
    { x: 210, y: 62, label: 'idea' },
    { x: 330, y: 175, label: 'build' },
    { x: 210, y: 288, label: 'ship' },
    { x: 90, y: 175, label: 'design' },
  ];
  return (
    <svg viewBox="0 0 420 360" style={svgStyle} role="img" aria-label="End-to-end development illustration">
      <Defs id={id} />
      <circle cx="210" cy="175" r="155" fill={`url(#${id}-glow)`} />

      {/* rotating orbit */}
      <g style={{ transformOrigin: '210px 175px', animation: 'fx-spin-slow 30s linear infinite' }}>
        <circle cx="210" cy="175" r="118" fill="none" stroke={`url(#${id}-grad)`} strokeWidth="1.5" strokeDasharray="3 12" opacity="0.55" />
      </g>

      {/* flowing pipeline path (diamond loop) */}
      <path
        d="M210 62 L330 175 L210 288 L90 175 Z"
        fill="none" stroke={`url(#${id}-grad)`} strokeWidth="2.5"
        strokeLinecap="round" strokeDasharray="10 12"
        style={{ animation: 'fx-dash 10s linear infinite' }}
        opacity="0.9"
      />

      {/* platform hub center */}
      <g style={{ animation: 'fx-float-soft 6s ease-in-out infinite' }}>
        <circle cx="210" cy="175" r="46" fill="hsl(224,45%,12%)" stroke={`url(#${id}-grad)`} strokeWidth="2.5" />
        <circle cx="210" cy="175" r="46" fill={`url(#${id}-grad)`} opacity="0.12" />
        {/* infinity glyph = continuous delivery */}
        <path
          d="M192 175 a11 11 0 1 1 11 11 a11 11 0 1 0 11 -11 a11 11 0 1 1 -11 -11 a11 11 0 1 0 -11 11 z"
          fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" opacity="0.95"
        />
      </g>

      {/* lifecycle nodes */}
      {nodes.map((n, i) => (
        <g key={n.label} style={{ animation: `fx-float-soft 5s ease-in-out infinite ${i * 0.4}s` }}>
          <circle cx={n.x} cy={n.y} r="26" fill="hsl(224,45%,13%)" stroke={`url(#${i % 2 ? `${id}-grad2` : `${id}-grad`})`} strokeWidth="2.5" />
          <NodeGlyph label={n.label} cx={n.x} cy={n.y} id={id} />
        </g>
      ))}

      {/* travelling pulse dots along the loop */}
      <circle r="4.5" fill="hsl(190,95%,60%)">
        <animateMotion dur="6s" repeatCount="indefinite" path="M210 62 L330 175 L210 288 L90 175 Z" />
      </circle>
      <circle r="3.5" fill="hsl(265,90%,68%)">
        <animateMotion dur="6s" begin="3s" repeatCount="indefinite" path="M210 62 L330 175 L210 288 L90 175 Z" />
      </circle>
    </svg>
  );
}

/* ---------- 5. AI & Machine Learning ---------- */
function Sparkle({ cx, cy, s, fill, delay = 0 }) {
  return (
    <path
      d={`M${cx} ${cy - s} Q${cx} ${cy} ${cx + s} ${cy} Q${cx} ${cy} ${cx} ${cy + s} Q${cx} ${cy} ${cx - s} ${cy} Q${cx} ${cy} ${cx} ${cy - s} Z`}
      fill={fill}
      style={{ transformOrigin: `${cx}px ${cy}px`, animation: `fx-pulse-glow 2.6s ease-in-out infinite ${delay}s` }}
    />
  );
}

export function AIIllustration() {
  const id = 'fx-ai';
  const L1 = [{ x: 108, y: 120 }, { x: 108, y: 180 }, { x: 108, y: 240 }];
  const L2 = [{ x: 210, y: 90 }, { x: 210, y: 150 }, { x: 210, y: 210 }, { x: 210, y: 270 }];
  const L3 = [{ x: 312, y: 150 }, { x: 312, y: 210 }];
  const edges = [];
  L1.forEach((a) => L2.forEach((b) => edges.push([a, b])));
  L2.forEach((a) => L3.forEach((b) => edges.push([a, b])));

  return (
    <svg viewBox="0 0 420 360" style={svgStyle} role="img" aria-label="AI and machine learning illustration">
      <Defs id={id} />
      <circle cx="210" cy="180" r="155" fill={`url(#${id}-glow)`} />

      {/* rotating orbit */}
      <g style={{ transformOrigin: '210px 180px', animation: 'fx-spin-slow 32s linear infinite' }}>
        <circle cx="210" cy="180" r="132" fill="none" stroke={`url(#${id}-grad2)`} strokeWidth="1.5" strokeDasharray="2 12" opacity="0.5" />
      </g>

      {/* synapse edges */}
      <g stroke={`url(#${id}-grad)`} strokeWidth="1.4" fill="none" strokeLinecap="round"
         strokeDasharray="5 9" style={{ animation: 'fx-dash 9s linear infinite' }} opacity="0.5">
        {edges.map(([a, b], i) => (
          <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />
        ))}
      </g>

      {/* neuron nodes */}
      {L1.map((n, i) => (
        <circle key={`l1-${i}`} cx={n.x} cy={n.y} r="10" fill="hsl(224,45%,12%)" stroke={`url(#${id}-grad2)`} strokeWidth="2.5"
          style={{ animation: `fx-float-soft 5s ease-in-out infinite ${i * 0.3}s` }} />
      ))}
      {L3.map((n, i) => (
        <g key={`l3-${i}`} style={{ animation: `fx-float-soft 5s ease-in-out infinite ${i * 0.4 + 0.5}s` }}>
          <circle cx={n.x} cy={n.y} r="12" fill="hsl(224,45%,12%)" stroke={`url(#${id}-grad)`} strokeWidth="2.5" />
          <circle cx={n.x} cy={n.y} r="4" fill="hsl(150,70%,52%)" />
        </g>
      ))}
      {L2.map((n, i) => (
        <g key={`l2-${i}`} style={{ animation: `fx-float-soft 4.5s ease-in-out infinite ${i * 0.25}s` }}>
          <circle cx={n.x} cy={n.y} r="15" fill={`url(#${id}-grad)`} opacity="0.9" />
          <circle cx={n.x} cy={n.y} r="15" fill="none" stroke="#fff" strokeWidth="1" opacity="0.3" />
          <circle cx={n.x - 4} cy={n.y - 4} r="3.5" fill="#fff" opacity="0.6" />
        </g>
      ))}

      {/* central AI core chip */}
      <g style={{ animation: 'fx-float-soft 6s ease-in-out infinite' }}>
        <rect x="182" y="152" width="56" height="56" rx="14" fill="hsl(224,48%,10%)" stroke={`url(#${id}-grad)`} strokeWidth="2.5" />
        {/* chip pins */}
        <g stroke={`url(#${id}-grad2)`} strokeWidth="2.5" strokeLinecap="round">
          <line x1="196" y1="152" x2="196" y2="144" />
          <line x1="210" y1="152" x2="210" y2="142" />
          <line x1="224" y1="152" x2="224" y2="144" />
          <line x1="196" y1="208" x2="196" y2="216" />
          <line x1="210" y1="208" x2="210" y2="218" />
          <line x1="224" y1="208" x2="224" y2="216" />
          <line x1="182" y1="167" x2="174" y2="167" />
          <line x1="182" y1="193" x2="174" y2="193" />
          <line x1="238" y1="167" x2="246" y2="167" />
          <line x1="238" y1="193" x2="246" y2="193" />
        </g>
        {/* spark core */}
        <Sparkle cx={210} cy={180} s={16} fill="#fff" />
        <Sparkle cx={210} cy={180} s={9} fill="hsl(190,95%,70%)" delay={0.4} />
      </g>

      {/* floating sparkles */}
      <Sparkle cx={132} cy={92} s={9} fill="hsl(265,90%,70%)" delay={0.2} />
      <Sparkle cx={300} cy={100} s={7} fill="hsl(190,95%,62%)" delay={1} />
      <Sparkle cx={324} cy={268} s={8} fill="hsl(210,98%,64%)" delay={1.6} />
      <Sparkle cx={96} cy={286} s={6} fill="hsl(280,88%,68%)" delay={0.8} />
    </svg>
  );
}

function NodeGlyph({ label, cx, cy, id }) {
  const stroke = '#fff';
  const props = { fill: 'none', stroke, strokeWidth: 2.6, strokeLinecap: 'round', strokeLinejoin: 'round', opacity: 0.95 };
  if (label === 'idea') {
    // lightbulb
    return (
      <g {...props}>
        <path d={`M${cx - 8} ${cy + 2} a8 8 0 1 1 16 0 c0 4 -3 6 -4 8 h-8 c-1 -2 -4 -4 -4 -8 z`} />
        <path d={`M${cx - 3} ${cy + 12} h6`} />
      </g>
    );
  }
  if (label === 'build') {
    // gear
    return (
      <g style={{ transformOrigin: `${cx}px ${cy}px`, animation: 'fx-spin-slow 8s linear infinite' }} {...props}>
        <circle cx={cx} cy={cy} r="5.5" />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
          const r1 = 8;
          const r2 = 12;
          const rad = (a * Math.PI) / 180;
          return (
            <line
              key={a}
              x1={cx + r1 * Math.cos(rad)} y1={cy + r1 * Math.sin(rad)}
              x2={cx + r2 * Math.cos(rad)} y2={cy + r2 * Math.sin(rad)}
            />
          );
        })}
      </g>
    );
  }
  if (label === 'ship') {
    // rocket
    return (
      <g {...props}>
        <path d={`M${cx} ${cy - 12} c6 4 6 12 4 18 h-8 c-2 -6 -2 -14 4 -18 z`} />
        <path d={`M${cx - 4} ${cy + 6} l-4 6 M${cx + 4} ${cy + 6} l4 6`} />
        <circle cx={cx} cy={cy - 2} r="2.4" fill={stroke} stroke="none" />
      </g>
    );
  }
  // design — pen/ruler
  return (
    <g {...props}>
      <path d={`M${cx - 9} ${cy + 9} l14 -14 l4 4 l-14 14 z`} />
      <path d={`M${cx + 5} ${cy - 5} l4 4`} />
      <path d={`M${cx - 9} ${cy + 9} l-2 6 l6 -2`} />
    </g>
  );
}
